import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  enableSpookyTheme,
  isLaunchpadExpandedHitAreaEnabled,
  isTypographyRefreshEnabled,
  launchpadButtonExperiment,
} from '@gonfalon/dogfood-flags/dogfood-flags';

import { syncSpookyTheme } from './internal/spookyTheme/syncSpookyTheme';
import { ThemeContext } from './internal/ThemeContext';
import type { Theme, ThemeSetting } from './types';

const isValidTheme = (theme: string): theme is ThemeSetting =>
  ['system', 'default', 'dark'].includes(theme) || (theme === 'spooky' && enableSpookyTheme());

const getFallbackTheme: () => ThemeSetting = () => 'system';

const loadThemeFromLocalStorage: () => ThemeSetting = () => {
  if (!isValidTheme(localStorage.theme)) {
    delete localStorage.theme;
  }

  const storedTheme = localStorage.theme as ThemeSetting;

  return storedTheme || getFallbackTheme();
};

const getSystemTheme = (): Theme => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'default';
  }
  return 'default';
};

const getResolvedTheme = (theme: ThemeSetting): Theme => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  if (theme === 'spooky') {
    return 'dark';
  }
  return theme;
};

const getInitialTheme = (forceTheme?: ThemeSetting) => forceTheme || loadThemeFromLocalStorage();

export const ThemeProvider = ({ children, forceTheme }: { children: ReactNode; forceTheme?: ThemeSetting }) => {
  const [themeSetting, setThemeSettingState] = useState<ThemeSetting>(getInitialTheme(forceTheme));
  const [theme, setTheme] = useState<Theme>(() => getResolvedTheme(getInitialTheme(forceTheme)));

  const setThemeSetting = useCallback(
    (newTheme: ThemeSetting) => {
      const validTheme = isValidTheme(newTheme) ? newTheme : getFallbackTheme();
      setThemeSettingState(validTheme);
      setTheme(getResolvedTheme(validTheme));
    },
    [setThemeSettingState, setTheme],
  );

  // Listen for system theme changes when theme is set to 'system'
  useEffect(() => {
    if (themeSetting !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setTheme(getSystemTheme());
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeSetting]);

  useEffect(() => {
    if (isTypographyRefreshEnabled()) {
      document.documentElement.dataset.typeRefresh = '';
    }
    if (isLaunchpadExpandedHitAreaEnabled()) {
      document.documentElement.dataset.lpHitArea = 'expanded';
    }
    // Names the live Button experiment for static/ld/stylesheets/button-experiments.css. Absent
    // rather than `off` when there isn't one, so the attribute selectors never have to spell out
    // an inactive value.
    const buttonExperiment = launchpadButtonExperiment();

    if (buttonExperiment === 'off') {
      document.documentElement.removeAttribute('data-button-experiment');
    } else {
      document.documentElement.setAttribute('data-button-experiment', buttonExperiment);
    }
  }, []);

  useEffect(() => {
    /*
      Don't update local storage if the forceTheme prop is defined.
      Updating this value will overwrite the user's preference.
    */
    if (!forceTheme) {
      localStorage.theme = themeSetting;
    }

    document.documentElement.setAttribute('data-theme', theme);

    if (enableSpookyTheme()) {
      syncSpookyTheme(themeSetting === 'spooky');
    }
  }, [themeSetting, theme, forceTheme]);

  useEffect(() => {
    // Don't set theme from local storage if the forceTheme prop is defined
    if (forceTheme) {
      return;
    }

    const onStorage = () => {
      const val = localStorage.theme;
      if (isValidTheme(val)) {
        setThemeSetting(val);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [setThemeSetting, forceTheme]);

  const value = useMemo(
    () => ({
      themeSetting,
      theme,
      setTheme: setThemeSetting,
    }),
    [themeSetting, theme, setThemeSetting],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
